/* eslint-disable @typescript-eslint/no-this-alias */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { useEffect, useRef, useState } from 'react'

export const useDebounceCallback = <T extends (...args: any[]) => void>(
  callback: T,
  delay: number = 301,
): ((...args: Parameters<T>) => void) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const debouncedCallback = (...args: Parameters<T>) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(() => {
      callback(...args)
    }, delay)
  }

  return debouncedCallback
}
type DebounceOptions = {
  immediate?: boolean
}

type DebouncedFunction<T extends (...args: any[]) => any> = {
  (...args: Parameters<T>): ReturnType<T>
  clear: () => void
  flush: () => void
  trigger: () => void
  readonly isPending: boolean
}

export function debounce<T extends (...args: any[]) => any>(
  function_: T,
  wait = 100,
  options: DebounceOptions | boolean = {},
): DebouncedFunction<T> {
  if (typeof function_ !== 'function') {
    throw new TypeError(
      `Expected the first parameter to be a function, got \`${typeof function_}\`.`,
    )
  }

  if (wait < 0) {
    throw new RangeError('`wait` must not be negative.')
  }

  const { immediate } =
    typeof options === 'boolean' ? { immediate: options } : options

  let storedContext: any
  let storedArguments: any
  let timeoutId: ReturnType<typeof setTimeout> | undefined
  let timestamp: number
  let result: ReturnType<T>

  function run() {
    const callContext = storedContext
    const callArguments = storedArguments
    storedContext = undefined
    storedArguments = undefined
    result = function_.apply(callContext, callArguments)
    return result
  }

  function later() {
    const last = Date.now() - timestamp

    if (last < wait && last >= 0) {
      timeoutId = setTimeout(later, wait - last)
    } else {
      timeoutId = undefined

      if (!immediate) {
        result = run()
      }
    }
  }

  const debounced = function (...args: Parameters<T>): ReturnType<T> {
    if (
      storedContext &&
      this !== storedContext &&
      Object.getPrototypeOf(this) === Object.getPrototypeOf(storedContext)
    ) {
      throw new Error(
        'Debounced method called with different contexts of the same prototype.',
      )
    }

    storedContext = this
    storedArguments = args
    timestamp = Date.now()

    const callNow = immediate && !timeoutId

    if (!timeoutId) {
      timeoutId = setTimeout(later, wait)
    }

    if (callNow) {
      result = run()
    }

    return result
  } as DebouncedFunction<T>

  Object.defineProperty(debounced, 'isPending', {
    get() {
      return timeoutId !== undefined
    },
  })

  debounced.clear = () => {
    if (!timeoutId) {
      return
    }

    clearTimeout(timeoutId)
    timeoutId = undefined
  }

  debounced.flush = () => {
    if (!timeoutId) {
      return
    }

    debounced.trigger()
  }

  debounced.trigger = () => {
    result = run()
    debounced.clear()
  }

  return debounced
}

export function useDebouncedValue<T>(value: T, delay = 1000): T {
  const [_value, setDebouncedValue] = useState(value)

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return _value
}
